import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  AlertCircle,
  ArrowRight,
  Check,
  Loader2,
  Search,
  ShieldCheck,
  Sparkles,
  X,
} from 'lucide-react';
import {
  estimateExportEmailConfidence,
  ExportEmailConfidenceEstimate,
  ExportSelection,
} from '../../../api/results';
import { ResultsFilter, ResultsSummary } from '../../../types/results.types';
import { usePostHog } from 'posthog-js/react';

interface ExportEmailConfidenceModalProps {
  isOpen: boolean;
  onClose: () => void;
  searchId: number;
  format: 'csv' | 'excel';
  filters?: ResultsFilter;
  filteredLeadCount: number;
  onExport: (
    format: 'csv' | 'excel',
    selection: ExportSelection,
  ) => void | Promise<void>;
  onVerifyClick?: () => void;
  onEnrichClick?: () => void;
  summary?: ResultsSummary | null;
  verifiableCount?: number;
  enrichableCount?: number;
}

type ModalStep = 'nudge' | 'preparing' | 'success' | 'error';

const ExportEmailConfidenceModal: React.FC<ExportEmailConfidenceModalProps> = ({
  isOpen,
  onClose,
  searchId,
  format,
  filters,
  filteredLeadCount: filteredLeadCountProp,
  onExport,
  onVerifyClick,
  onEnrichClick,
  summary,
  verifiableCount: verifiableCountProp,
  enrichableCount: enrichableCountProp,
}) => {
  const [estimate, setEstimate] = useState<ExportEmailConfidenceEstimate | null>(null);
  const [loading, setLoading] = useState(false);
  const [estimateError, setEstimateError] = useState(false);
  const [step, setStep] = useState<ModalStep>('nudge');
  const [includeSharedInboxes, setIncludeSharedInboxes] = useState(true);
  const [includeUnverifiedDirect, setIncludeUnverifiedDirect] = useState(false);
  const inFlightRef = useRef(false);
  const estimateRequestRef = useRef(0);
  const lastEstimateKeyRef = useRef<string | null>(null);
  const initialFocusRef = useRef<HTMLButtonElement>(null);
  const posthog = usePostHog();
  const filtersRef = useRef(filters);
  const posthogRef = useRef(posthog);
  filtersRef.current = filters;
  posthogRef.current = posthog;
  const filtersKey = JSON.stringify(filters ?? {});

  useEffect(() => {
    if (!isOpen || searchId <= 0) {
      lastEstimateKeyRef.current = null;
      return;
    }
    if (inFlightRef.current) {
      setStep('preparing');
      return;
    }

    const estimateKey = `${searchId}:${format}:${filtersKey}`;
    if (lastEstimateKeyRef.current === estimateKey) return;
    lastEstimateKeyRef.current = estimateKey;

    const requestId = estimateRequestRef.current + 1;
    estimateRequestRef.current = requestId;
    setEstimate(null);
    setEstimateError(false);
    setStep('nudge');
    setIncludeSharedInboxes(true);
    setIncludeUnverifiedDirect(false);
    setLoading(true);

    estimateExportEmailConfidence(searchId, filtersRef.current)
      .then((data) => {
        if (estimateRequestRef.current !== requestId) return;
        setEstimate(data);
        posthogRef.current?.capture('export_quality_modal_shown', {
          unverified_email_count: data.unverified_count,
          verification_eligible_count: data.verification_eligible_count,
          enrichable_people_count: data.enrichable_people_count,
          format,
        });
        posthogRef.current?.capture('export_modal_opened', {
          ready_now_count: data.ready_now_count,
          ready_personal_count: data.ready_personal_count,
          ready_company_count: data.ready_company_count,
          expanded_export_count: data.expanded_export_count,
          verifiable_count: data.verification_eligible_count,
          enrichable_count: data.enrichable_people_count,
          filtered_lead_count: data.filtered_lead_count,
          ready_decision_maker_email_count: data.ready_decision_maker_email_count,
          ready_other_direct_email_count: data.ready_other_direct_email_count,
          shared_inbox_count: data.shared_inbox_count,
          unverified_direct_email_count: data.unverified_direct_email_count,
          format,
        });
      })
      .catch(() => {
        if (estimateRequestRef.current === requestId) {
          setEstimateError(true);
        }
      })
      .finally(() => {
        if (estimateRequestRef.current !== requestId) return;
        setLoading(false);
        posthogRef.current?.capture('export_estimate_loaded', { search_id: searchId, format });
      });
  }, [isOpen, searchId, format, filtersKey]);

  const readyDecisionMakers =
    estimate?.ready_decision_maker_email_count ??
    summary?.ready_personal_count ??
    0;
  const readyOtherDirect = estimate?.ready_other_direct_email_count ?? 0;
  const sharedInboxes = estimate?.shared_inbox_count ?? 0;
  const unverifiedDirect = estimate?.unverified_direct_email_count ?? 0;
  const filteredLeadCount =
    estimate?.filtered_lead_count ?? filteredLeadCountProp;
  const verifiableTotal =
    estimate?.verification_eligible_count ??
    verifiableCountProp ??
    estimate?.unverified_count ??
    0;
  const enrichableTotal =
    enrichableCountProp ?? estimate?.enrichable_people_count ?? 0;
  const includedEmailCount =
    readyDecisionMakers +
    readyOtherDirect +
    (includeSharedInboxes ? sharedInboxes : 0) +
    (includeUnverifiedDirect ? unverifiedDirect : 0);
  const coverage = includeUnverifiedDirect ? 'expanded' : 'ready';

  const selection: ExportSelection = {
    coverage,
    includeSharedInboxes,
    includedEmailCount,
    filteredLeadCount,
    readyDecisionMakerEmailCount: readyDecisionMakers,
    readyOtherDirectEmailCount: readyOtherDirect,
    sharedInboxCount: sharedInboxes,
    unverifiedDirectEmailCount: unverifiedDirect,
  };

  const close = () => {
    onClose();
  };

  const handleGrowFirst = (action: 'find' | 'verify') => {
    posthog?.capture('export_improve_first_clicked', {
      search_id: searchId,
      action,
      ready_now_count: readyDecisionMakers + readyOtherDirect,
      verify_capacity: verifiableTotal,
      find_capacity: enrichableTotal,
      enrichable_people_count: enrichableTotal,
      unverified_email_count: verifiableTotal,
    });
    onClose();
    if (action === 'find') {
      onEnrichClick?.();
    } else {
      onVerifyClick?.();
    }
  };

  const handleExport = async () => {
    if (inFlightRef.current || loading) return;
    inFlightRef.current = true;
    setStep('preparing');
    const startedAt = Date.now();
    posthog?.capture('export_preparing_started', {
      search_id: searchId,
      format,
      coverage,
      include_shared_inboxes: includeSharedInboxes,
      included_email_count: includedEmailCount,
      filtered_lead_count: filteredLeadCount,
    });
    try {
      await onExport(format, selection);
      setStep('success');
      posthog?.capture('export_succeeded', {
        search_id: searchId,
        format,
        coverage,
        include_shared_inboxes: includeSharedInboxes,
        included_email_count: includedEmailCount,
        filtered_lead_count: filteredLeadCount,
        duration_ms: Date.now() - startedAt,
      });
    } catch (error: unknown) {
      const errorClass =
        error instanceof Error ? error.constructor.name : 'UnknownError';
      setStep('error');
      posthog?.capture('export_failed', {
        search_id: searchId,
        format,
        coverage,
        include_shared_inboxes: includeSharedInboxes,
        error_class: errorClass,
      });
    } finally {
      inFlightRef.current = false;
    }
  };

  const handleRetry = () => {
    posthog?.capture('export_try_again_clicked', {
      search_id: searchId,
      format,
      coverage,
      include_shared_inboxes: includeSharedInboxes,
    });
    handleExport();
  };

  const renderDecisionMakerBanner = () => {
    if (enrichableTotal === 0 && readyDecisionMakers > 0) {
      return (
        <div className="mb-4 flex items-center gap-3 rounded-[13px] border border-emerald-200 bg-emerald-50 px-3 py-2.5">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-600">
            <Check className="h-4 w-4 text-white" strokeWidth={3} />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-bold text-emerald-800">
              {readyDecisionMakers.toLocaleString()} decision-maker{' '}
              {readyDecisionMakers === 1 ? 'email' : 'emails'} ready
            </p>
            <p className="mt-0.5 text-xs leading-5 text-emerald-700">
              All currently discovered decision-maker contacts are ready.
            </p>
          </div>
        </div>
      );
    }

    if (enrichableTotal <= 0) return null;
    const hasReadyDecisionMakers = readyDecisionMakers > 0;

    return (
      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-[13px] border border-[#EFD9D9] bg-[#FBF1F1] px-3 py-2.5">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary">
          {hasReadyDecisionMakers ? (
            <Sparkles className="h-4 w-4 text-white" />
          ) : (
            <Search className="h-4 w-4 text-white" />
          )}
        </span>
        <div className="min-w-[180px] flex-1">
          <p className="text-sm font-bold text-primary">
            {hasReadyDecisionMakers
              ? `${readyDecisionMakers.toLocaleString()} decision-maker ${
                  readyDecisionMakers === 1 ? 'email' : 'emails'
                } ready`
              : 'No decision-maker emails ready yet'}
          </p>
          <p className="mt-0.5 text-xs leading-5 text-[#875A5C]">
            {enrichableTotal.toLocaleString()} more decision{' '}
            {enrichableTotal === 1 ? 'maker can' : 'makers can'} still be searched.
          </p>
        </div>
        <button
          type="button"
          onClick={() => handleGrowFirst('find')}
          className="inline-flex min-h-11 w-full items-center justify-center gap-1.5 rounded-lg bg-primary px-3.5 py-2 text-xs font-bold text-white transition-colors hover:bg-primary-hover active:bg-[#5F1F22] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary sm:ml-auto sm:w-auto"
        >
          {hasReadyDecisionMakers
            ? `Find ${enrichableTotal.toLocaleString()} more`
            : 'Find decision makers'}
          <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  };

  const renderIncludedRow = (count: number, label: string) => {
    if (count <= 0) return null;
    return (
      <div className="flex items-center gap-2.5">
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-600">
          <Check className="h-3 w-3 text-white" strokeWidth={3} />
        </span>
        <span className="text-[13px] text-gray-700">
          <strong className="font-bold text-[#1C1B22]">
            {count.toLocaleString()} {label}
          </strong>
        </span>
      </div>
    );
  };

  const renderOption = (
    id: string,
    checked: boolean,
    onChange: (checked: boolean) => void,
    title: string,
    description: string,
  ) => (
    <label
      htmlFor={id}
      className="flex min-h-11 cursor-pointer items-start gap-3 px-4 py-3 transition-colors hover:bg-[#FBFAFA] active:bg-[#F5F2F2]"
    >
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-0.5 h-5 w-5 shrink-0 rounded-md border-gray-300 text-primary focus:ring-2 focus:ring-primary focus:ring-offset-2"
      />
      <span className="min-w-0">
        <span className="block text-[13px] font-semibold text-[#1C1B22]">
          {title}
        </span>
        <span className="mt-0.5 block text-xs leading-5 text-gray-500">
          {description}
        </span>
      </span>
    </label>
  );

  const renderEstimateWarning = () => (
    <div className="mb-4 flex items-start gap-3 rounded-[13px] border border-amber-200 bg-amber-50 px-3 py-2.5">
      <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
      <div>
        <p className="text-sm font-bold text-amber-900">
          Email options couldn&apos;t be loaded
        </p>
        <p className="mt-0.5 text-xs leading-5 text-amber-800">
          You can still export every lead with the safe default. Shared inboxes
          and unverified emails will stay excluded.
        </p>
      </div>
    </div>
  );

  const renderNudge = () => (
    <>
      <div className="flex shrink-0 items-start justify-between gap-4 px-6 pb-3.5 pt-5">
        <div>
          <Dialog.Title className="text-[19px] font-extrabold leading-tight tracking-tight text-[#1C1B22]">
            Export your leads
          </Dialog.Title>
          <Dialog.Description className="mt-1 text-[13px] text-gray-500">
            Choose which email addresses to include in your file.
          </Dialog.Description>
        </div>
        <button
          ref={initialFocusRef}
          type="button"
          onClick={close}
          className="-mr-2 -mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-600 active:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          aria-label="Close export dialog"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-2">
        {loading && !estimate ? (
          <div aria-busy="true" className="space-y-3 pb-4 pt-1">
            <div className="h-16 animate-pulse rounded-[13px] bg-gray-100 motion-reduce:animate-none" />
            <div className="h-44 animate-pulse rounded-2xl border border-gray-100 bg-gray-50 motion-reduce:animate-none" />
            <span className="sr-only" role="status">
              Estimating your export…
            </span>
          </div>
        ) : (
          <>
            {estimateError && renderEstimateWarning()}
            {renderDecisionMakerBanner()}
            {!estimateError && (
              <div className="mb-1 overflow-hidden rounded-2xl border border-[#ECEAEA] bg-white">
                  <div className="flex items-end justify-between gap-3 px-4 pb-3 pt-4">
                    <div className="flex items-baseline gap-2">
                      <span
                        className="text-[40px] font-extrabold leading-none tracking-tight text-[#1C1B22]"
                        aria-live="polite"
                      >
                        {includedEmailCount.toLocaleString()}
                      </span>
                      <span className="text-sm font-medium text-gray-500">
                        Email addresses included
                      </span>
                    </div>
                    <span className="hidden pb-1 text-[11px] font-semibold text-gray-400 sm:block">
                      Included across exported leads.
                    </span>
                  </div>

                  {(readyDecisionMakers > 0 || readyOtherDirect > 0) && (
                    <div className="space-y-2 px-4 pb-3">
                      {renderIncludedRow(
                        readyDecisionMakers,
                        `verified decision-maker ${
                          readyDecisionMakers === 1 ? 'email' : 'emails'
                        }`,
                      )}
                      {renderIncludedRow(
                        readyOtherDirect,
                        `other verified direct ${
                          readyOtherDirect === 1 ? 'email' : 'emails'
                        }`,
                      )}
                    </div>
                  )}

                  {(sharedInboxes > 0 || unverifiedDirect > 0) && (
                    <>
                      <div className="mx-4 h-px bg-[#F0EDED]" />
                      <div>
                        {sharedInboxes > 0 &&
                          renderOption(
                            `export-shared-${searchId}`,
                            includeSharedInboxes,
                            setIncludeSharedInboxes,
                            `Include ${sharedInboxes.toLocaleString()} shared ${
                              sharedInboxes === 1 ? 'inbox' : 'inboxes'
                            }`,
                            'Addresses like info@ and support@. Useful as a fallback, but not tied to a person.',
                          )}
                        {sharedInboxes > 0 && unverifiedDirect > 0 && (
                          <div className="mx-4 h-px bg-[#F0EDED]" />
                        )}
                        {unverifiedDirect > 0 && (
                          renderOption(
                            `export-unverified-${searchId}`,
                            includeUnverifiedDirect,
                            setIncludeUnverifiedDirect,
                            `Include ${unverifiedDirect.toLocaleString()} unverified direct ${
                              unverifiedDirect === 1 ? 'email' : 'emails'
                            }`,
                            'Inbox not confirmed; may increase bounce risk.',
                          )
                        )}
                      </div>
                    </>
                  )}
              </div>
            )}
            {!estimateError && verifiableTotal > 0 && (
              <div className="mb-1 mt-3 flex flex-wrap items-center gap-3 rounded-[13px] border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-[#E5E7EB] bg-white">
                  <ShieldCheck className="h-4 w-4 text-[#4B5563]" />
                </span>
                <div className="min-w-[180px] flex-1">
                  <p className="text-[13px] font-bold text-[#1C1B22]">
                    {verifiableTotal.toLocaleString()} email
                    {verifiableTotal === 1 ? ' is' : 's are'} eligible for verification
                  </p>
                  <p className="mt-0.5 text-xs leading-5 text-gray-500">
                    Verify all eligible emails across the filtered results.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleGrowFirst('verify')}
                  className="inline-flex min-h-11 w-full items-center justify-center rounded-lg border border-[#D1D5DB] bg-white px-3.5 py-2 text-xs font-bold text-[#374151] transition-colors hover:bg-gray-50 active:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary sm:ml-auto sm:w-auto"
                >
                  Verify {verifiableTotal.toLocaleString()} emails
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <div className="shrink-0 border-t border-[#F0EDED] bg-white px-6 pb-4 pt-3">
        {!loading && (
          <>
            <p className="mb-2.5 text-xs text-gray-500">
              {filteredLeadCount.toLocaleString()}{' '}
              {filteredLeadCount === 1 ? 'lead' : 'leads'} will be exported
              {!estimateError &&
                includedEmailCount > 0 &&
                ` · ${includedEmailCount.toLocaleString()} email ${
                  includedEmailCount === 1 ? 'address' : 'addresses'
                } included`}
            </p>
            {!estimateError && includedEmailCount === 0 && (
              <p className="mb-2.5 text-xs leading-5 text-amber-800">
                No email addresses selected. Business details, phone numbers,
                websites, and addresses will still be exported.
              </p>
            )}
          </>
        )}
        <button
          type="button"
          onClick={handleExport}
          disabled={loading}
          className="min-h-11 w-full rounded-xl bg-primary px-4 py-3 text-sm font-bold text-white transition-colors hover:bg-primary-hover active:bg-[#5F1F22] disabled:cursor-not-allowed disabled:bg-gray-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        >
          {loading
            ? 'Estimating export…'
            : `Export ${filteredLeadCount.toLocaleString()} ${
                filteredLeadCount === 1 ? 'lead' : 'leads'
              }`}
        </button>
        <button
          type="button"
          onClick={close}
          className="mt-1.5 min-h-11 w-full rounded-lg text-[13px] font-medium text-gray-400 transition-colors hover:text-gray-700 active:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        >
          Cancel
        </button>
      </div>
    </>
  );

  const renderProcessState = () => {
    if (step === 'preparing') {
      return (
        <div className="px-8 py-11 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl border border-[#EFD9D9] bg-[#FBF1F1]">
            <Loader2 className="h-8 w-8 animate-spin text-primary motion-reduce:animate-none" />
          </div>
          <Dialog.Title className="mt-5 text-xl font-extrabold text-[#1C1B22]">
            Preparing your export…
          </Dialog.Title>
          <Dialog.Description
            role="status"
            className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-gray-500"
          >
            Building your {format.toUpperCase()}. Larger exports may take up to a
            minute.
          </Dialog.Description>
          <button
            ref={initialFocusRef}
            type="button"
            onClick={close}
            className="mt-6 min-h-11 rounded-lg px-4 text-sm text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            Hide
          </button>
        </div>
      );
    }

    if (step === 'success') {
      return (
        <div className="px-8 py-11 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl border border-emerald-200 bg-emerald-50">
            <ShieldCheck className="h-8 w-8 text-emerald-600" />
          </div>
          <Dialog.Title className="mt-5 text-xl font-extrabold text-[#1C1B22]">
            Export ready
          </Dialog.Title>
          <Dialog.Description
            role="status"
            className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-gray-500"
          >
            Your {format.toUpperCase()} download has started.
          </Dialog.Description>
          <button
            ref={initialFocusRef}
            type="button"
            onClick={onClose}
            className="mt-6 inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-primary px-5 text-sm font-bold text-white transition-colors hover:bg-primary-hover active:bg-[#5F1F22] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            Done
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      );
    }

    return (
      <div className="px-8 py-11 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl border border-red-200 bg-red-50">
          <AlertCircle className="h-8 w-8 text-red-500" />
        </div>
        <Dialog.Title className="mt-5 text-xl font-extrabold text-[#1C1B22]">
          Export failed
        </Dialog.Title>
        <Dialog.Description
          role="alert"
          className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-gray-500"
        >
          We couldn&apos;t create your {format.toUpperCase()}. Please try again.
        </Dialog.Description>
        <div className="mt-6 flex flex-col items-center gap-2">
          <button
            ref={initialFocusRef}
            type="button"
            onClick={handleRetry}
            className="min-h-11 rounded-xl bg-primary px-5 text-sm font-bold text-white transition-colors hover:bg-primary-hover active:bg-[#5F1F22] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            Try again
          </button>
          <button
            type="button"
            onClick={onClose}
            className="min-h-11 rounded-lg px-4 text-sm text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            Close
          </button>
        </div>
      </div>
    );
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[60]"
        initialFocus={initialFocusRef}
        onClose={close}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200 motion-reduce:transition-none"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150 motion-reduce:transition-none"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto p-2 sm:p-4">
          <div className="flex min-h-full items-center justify-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200 motion-reduce:transition-none"
              enterFrom="opacity-0 translate-y-2 scale-[0.98]"
              enterTo="opacity-100 translate-y-0 scale-100"
              leave="ease-in duration-150 motion-reduce:transition-none"
              leaveFrom="opacity-100 translate-y-0 scale-100"
              leaveTo="opacity-0 translate-y-2 scale-[0.98]"
            >
              <Dialog.Panel className="flex max-h-[calc(100vh-16px)] max-h-[calc(100dvh-16px)] w-[calc(100vw-16px)] max-w-[560px] flex-col overflow-hidden rounded-[22px] border border-[#EFE7E7] bg-white text-left shadow-2xl">
                {step === 'nudge' ? renderNudge() : renderProcessState()}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default ExportEmailConfidenceModal;
